// ABOUTME: The React Query hooks behind the applications list and one application's detail: they wait for the Auth0
// ABOUTME: token, ask in the app's language, and key the cache on both so a language switch or a new session refetches.
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { USE_FEED_QUERY_KEY } from 'modules/feed/constants';
import { getApiLocale } from 'modules/i18n';
import { useAccessToken } from 'utils/auth';
import { applicationsApi } from './client';
import {
  SET_APPLICATION_HIDDEN_MUTATION_KEY,
  UPDATE_APPLIED_WEEK_MUTATION_KEY,
  USE_APPLICATION_QUERY_KEY,
  USE_APPLICATIONS_QUERY_KEY,
  WITHDRAW_APPLICATION_MUTATION_KEY
} from './constants';
import { AppliedWeekUpdate, TalentApplication } from './types';

/**
 * The talent's applications. Not retried, for the same reason the detail below is not: the 404 that says PRO_ATS is
 * off for this talent is pro-core's settled answer, not a blip, and repeating it three times only holds the skeleton
 * on screen before the same off state appears.
 */
export const useApplicationsQuery = () => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<TalentApplication[]>(
    [USE_APPLICATIONS_QUERY_KEY, token, language],
    // `token!` is safe: the query only runs once `enabled` says there is one.
    () => applicationsApi.fetchApplications(token!, language),
    { enabled: !!token, retry: false }
  );
};

/**
 * One application's detail. Not retried: a 404 here is pro-core's answer about this refId, not a blip, and repeating
 * it only keeps the talent on a spinner before the same "we cannot find it".
 */
export const useApplicationQuery = (refId: string | undefined) => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<TalentApplication>(
    [USE_APPLICATION_QUERY_KEY, token, language, refId],
    // Both `!` are safe: the query only runs once `enabled` says there is a token and a refId.
    () => applicationsApi.fetchApplication(token!, language, refId!),
    { enabled: !!token && !!refId, retry: false }
  );
};

/**
 * The withdrawal. Its answer is the application as it reads afterwards, so it seeds the detail straight away and the
 * talent sees the withdrawn outcome without a second read; the list is invalidated because the same application sits
 * on it with a different tracker now.
 */
export const useWithdrawApplicationMutation = (refId: string | undefined) => {
  const { token } = useAccessToken();
  const language = getApiLocale();
  const queryClient = useQueryClient();

  return useMutation<TalentApplication, unknown, string>(
    [WITHDRAW_APPLICATION_MUTATION_KEY, token, refId],
    (reasonText: string) =>
      applicationsApi.withdrawApplication(token!, language, refId!, reasonText),
    {
      onSuccess: application => {
        queryClient.setQueryData(
          [USE_APPLICATION_QUERY_KEY, token, language, refId],
          application
        );
        void queryClient.invalidateQueries([USE_APPLICATIONS_QUERY_KEY]);
      }
    }
  );
};

/** PJL: the list with the hidden rows too, asked only once the talent says „Ausgeblendete anzeigen“. */
export const useApplicationsWithHiddenQuery = (enabled: boolean) => {
  const { token } = useAccessToken();
  const language = getApiLocale();

  return useQuery<TalentApplication[]>(
    [USE_APPLICATIONS_QUERY_KEY, token, language, 'includeHidden'],
    () =>
      applicationsApi.fetchApplications(token!, language, {
        includeHidden: true
      }),
    { enabled: !!token && enabled, retry: false }
  );
};

/**
 * PJL: hide an application from the talent's own list, or show it again. The list and the Jobs page's feed are read
 * again, since the row leaves or rejoins both; the detail is not, since hiding changes nothing there.
 */
export const useSetApplicationHiddenMutation = () => {
  const { token } = useAccessToken();
  const language = getApiLocale();
  const queryClient = useQueryClient();

  return useMutation<
    TalentApplication,
    unknown,
    { refId: string; hidden: boolean }
  >(
    [SET_APPLICATION_HIDDEN_MUTATION_KEY, token],
    ({ refId, hidden }) =>
      applicationsApi.setApplicationHidden(token!, language, refId, hidden),
    {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries([USE_APPLICATIONS_QUERY_KEY]),
          queryClient.invalidateQueries([USE_FEED_QUERY_KEY])
        ]);
      }
    }
  );
};

/** PJL: the week sent with an application, changed afterwards; the answer seeds the detail like the withdrawal does. */
export const useUpdateAppliedWeekMutation = (refId: string | undefined) => {
  const { token } = useAccessToken();
  const language = getApiLocale();
  const queryClient = useQueryClient();

  return useMutation<TalentApplication, unknown, AppliedWeekUpdate>(
    [UPDATE_APPLIED_WEEK_MUTATION_KEY, token, refId],
    week => applicationsApi.updateAppliedWeek(token!, language, refId!, week),
    {
      onSuccess: application => {
        queryClient.setQueryData(
          [USE_APPLICATION_QUERY_KEY, token, language, refId],
          application
        );
      }
    }
  );
};
